import { forwardRef, useEffect, useMemo, useState } from 'react';

import { Draggable } from '@hello-pangea/dnd';
import {
  Copy01Icon,
  Copy02Icon,
  Delete02Icon,
  DragDropVerticalIcon,
  GitBranchIcon,
  SlidersHorizontalIcon,
  SquareArrowDownDoubleIcon,
  SquareArrowUpDoubleIcon,
  SquareLock02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FieldTypes, FieldTypesOptions } from 'constants/field-types-enum';
import { typesWithLogic } from 'constants/logic';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';

import { Accordion } from 'components/Accordion/Accordion';
import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import { DateSettings } from 'components/Form/FormEdit/SettingsComponents/DateSettings/DateSettings';
import { DefaultSettings } from 'components/Form/FormEdit/SettingsComponents/DefaultSettings/DefaultSettings';
import { DownloadSettings } from 'components/Form/FormEdit/SettingsComponents/DownloadSettings/DownloadSettings';
import { MultipleChoiceSettings } from 'components/Form/FormEdit/SettingsComponents/MultipleChoiceSettings/MultipleChoiceSettings';
import { OpinionScaleSettings } from 'components/Form/FormEdit/SettingsComponents/OpinionScaleSettings/OpinionScaleSettings';
import { RulesConfiguration } from 'components/Form/FormEdit/SettingsComponents/RulesConfiguration/RulesConfiguration';
import { StarRatingSettings } from 'components/Form/FormEdit/SettingsComponents/StarRatingSettings/StarRatingSettings';
import {
  ParamTextarea,
  RichTextEditor,
  Select,
  Textarea,
  ValidationAlert,
} from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import { StatusPill } from 'components/Pills/StatusPills';

/** Split text by {{param}} placeholders for rendering with param-chip */
function parseParamText(text) {
  if (!text) return [];
  const segments = [];
  let lastIndex = 0;
  const re = /\{\{([^}]+)\}\}/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'param', key: match[1].trim() });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: text }];
}

// Action icon constants
const ACTION_ICONS = {
  LOGIC_JUMPS: GitBranchIcon,
  DUPLICATE: Copy01Icon,
  DELETE: Delete02Icon,
  LOCKED: SquareLock02Icon,
  RULES: SlidersHorizontalIcon,
};

const ACTION_COLORS = {
  DEFAULT: 'text-neutral-6',
  ERROR: 'text-red-2',
  SECONDARY: 'text-neutral-6',
};

const ACTION_BADGE_COLORS = {
  DEFAULT: 'bg-primary-4 text-white',
};

export const QuestionRow = forwardRef(
  (
    {
      formField,
      index,
      register,
      control,
      watch,
      setValue,
      unregister,
      remove,
      duplicate,
      copyField,
      pasteFieldAbove,
      pasteFieldBelow,
      active = false,
      logicJumpsCount = 0,
      rulesCount = 0,
      errors = {},
      validationErrorMessage = null,
      pasteEnabled = false,
      textParams = {},
      className,
      onClick = () => {},
      onChange = () => {},
      isDragging = false,
      minimal = false,
    },
    ref,
  ) => {
    const registerName = `fields.${index}`;
    const { fields } = useFieldArray({ control, name: 'fields' });

    const [isCollapsed, setIsCollapsed] = useState(!active);
    const field = useWatch({ control, name: `fields.${index}` });
    const typeValue = useWatch({ control, name: `${registerName}.type` });

    // Notify parent when field changes
    useEffect(() => {
      if (field) {
        onChange(field, index);
      }
    }, [field, index, onChange]);

    useEffect(() => {
      if (typeValue === FieldTypes.MultipleChoice) return;
      if (typeValue === FieldTypes.Statement) {
        const currentText = field?.text;
        if (currentText) {
          setValue(`${registerName}.text`, currentText);
        }
      }
      unregister(`${registerName}.options.options`);
    }, [typeValue, index, field?.text, registerName, setValue, unregister]);

    const isDragDisabled = field?.locked || field?.options?.intro;
    const isIntroField = field?.options?.intro;

    const actions = useMemo(() => {
      if (field == null) return [];
      return [
        ...(!isIntroField
          ? [
              {
                title: 'Duplicate',
                icon: Copy01Icon,
                onClick: () => duplicate(field),
              },
            ]
          : []),
        {
          title: 'Copy',
          icon: Copy02Icon,
          onClick: () => copyField(field),
        },
        {
          title: 'Paste above',
          icon: SquareArrowUpDoubleIcon,
          onClick: () => pasteFieldAbove(field),
          disabled: !pasteEnabled,
        },
        {
          title: 'Paste below',
          icon: SquareArrowDownDoubleIcon,
          onClick: () => pasteFieldBelow(field),
          disabled: !pasteEnabled,
        },
      ];
    }, [field, pasteEnabled, copyField, pasteFieldAbove, pasteFieldBelow, duplicate]);

    const noPlaceholderFields = [
      FieldTypes.Statement,
      FieldTypes.PDFPreview,
      FieldTypes.Download,
      FieldTypes.Upload,
      FieldTypes.Signature,
      FieldTypes.ReadOnly,
      FieldTypes.Hidden,
      FieldTypes.Checkbox,
      FieldTypes.DocumentUpload,
      FieldTypes.MultiUpload,
      FieldTypes.IdRequirementsList,
      FieldTypes.IdRequirementsWrapper,
      FieldTypes.OptionsList,
    ];

    const evaluatePlaceholder = useMemo(() => {
      if (noPlaceholderFields.includes(field?.type)) return true;
      return false;
    }, [field?.type]);

    const renderFieldSettings = () => {
      if (field == null) return null;
      if (field.locked) return null;

      switch (field?.type) {
        case FieldTypes.MultipleChoice:
          return (
            <MultipleChoiceSettings
              register={register}
              name={registerName}
              field={field}
              control={control}
              errors={errors}
            />
          );
        case FieldTypes.OpinionScale:
          return (
            <OpinionScaleSettings
              register={register}
              name={registerName}
              field={field}
              watch={watch}
            />
          );
        case FieldTypes.StarRating:
          return (
            <StarRatingSettings
              register={register}
              name={registerName}
              field={field}
              watch={watch}
            />
          );
        case FieldTypes.Date:
          return <DateSettings register={register} name={registerName} field={field} />;
        case FieldTypes.Download:
          return (
            <DownloadSettings
              register={register}
              name={registerName}
              field={field}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          );
        case FieldTypes.Statement:
          return null;
        default:
          return (
            <DefaultSettings
              register={register}
              name={registerName}
              field={field}
              noPlaceholder={evaluatePlaceholder}
            />
          );
      }
    };

    const actionIcons = useMemo(() => {
      const icons = [];

      if (!isIntroField) {
        icons.push({
          icon: ACTION_ICONS.LOGIC_JUMPS,
          tip: `${logicJumpsCount > 0 ? `${logicJumpsCount} logic jumps` : 'No logic jumps'}`,
          color: ACTION_COLORS.SECONDARY,
          badgeColor: ACTION_BADGE_COLORS.DEFAULT,
          onClick: null,
          badge: logicJumpsCount > 0 ? logicJumpsCount : null,
        });
      }

      if (!isIntroField) {
        icons.push({
          icon: ACTION_ICONS.RULES,
          tip: `${rulesCount > 0 ? `${rulesCount} rules` : 'No rules'}`,
          color: ACTION_COLORS.SECONDARY,
          badgeColor: ACTION_BADGE_COLORS.DEFAULT,
          onClick: null,
          badge: rulesCount > 0 ? rulesCount : null,
        });
      }

      icons.push({
        icon: ACTION_ICONS.DELETE,
        tip: 'Delete Field',
        color: ACTION_COLORS.ERROR,
        onClick: () => remove(index),
      });

      return icons;
    }, [isIntroField, logicJumpsCount, rulesCount, field, remove, index]);

    const renderActionIcon = ({ icon, tip, color, onClick, size = '20' }) => (
      <HugeiconsIcon
        icon={icon}
        onClick={onClick}
        className={cn('cursor-pointer', color, onClick && 'hover:opacity-70')}
        size={size}
        title={tip}
      />
    );

    if (field == null) return null;

    // Resolved field type option for the collapsed summary icon
    const fieldTypeOption = FieldTypesOptions.find(f => f.key === field.type);
    const plainText = field.text?.replace(/<[^>]+>/g, '') || 'Untitled question';

    // The header shown in both collapsed and expanded states
    const accordionHeader = (
      <div
        className={cn(
          'flex items-center gap-4 w-full min-w-0 relative',
          !isCollapsed && 'justify-between',
        )}>
        {!field.required && isCollapsed && (
          <StatusPill
            text="Optional"
            status="light"
            className="px-4 self-start absolute top-[-20px] text-sm font-semibold"
          />
        )}
        {/* Left: drag handle (hidden when minimal) + order number */}
        <div className="flex items-center gap-2 shrink-0">
          {!minimal && (
            <HugeiconsIcon
              icon={DragDropVerticalIcon}
              className="text-neutral-6"
              size="24"
              strokeWidth="2"
            />
          )}
          <span
            className="text-neutral-7 text-xl font-medium bg-primary-1 rounded-full w-12 h-12 flex items-center justify-center"
            data-testid="question-order-badge">
            {field?.order}
          </span>
        </div>

        {/* Middle: type icon + truncated question text */}
        <div
          className={cn(
            'flex items-center gap-2 min-w-0 flex-1 overflow-hidden bg-neutral-2 rounded-2xl px-3 py-4 border border-neutral-4',
            !isCollapsed && 'hidden',
          )}>
          {fieldTypeOption?.icon != null && (
            <HugeiconsIcon
              icon={fieldTypeOption.icon}
              size="16"
              className="text-neutral-7 shrink-0"
            />
          )}
          <span className="text-neutral-7 text-xl truncate">
            {parseParamText(plainText).map((seg, i) =>
              seg.type === 'text' ? (
                seg.value
              ) : (
                <span key={i} className="param-chip">
                  @ {seg.key}
                </span>
              ),
            )}
          </span>
        </div>

        {/* Right: action icons — stop propagation so clicks don't toggle accordion */}
        <div className="flex items-center gap-8 shrink-0" onClick={e => e.stopPropagation()}>
          {field.locked ? (
            <HugeiconsIcon
              icon={ACTION_ICONS.LOCKED}
              className="text-neutral-6"
              size="20"
              title="Field is locked"
              strokeWidth="2"
            />
          ) : (
            !minimal && (
              <>
                {actionIcons.map((actionIcon, idx) => (
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="top"
                    key={idx}
                    overlay={<Tooltip>{actionIcon.tip}</Tooltip>}>
                    <div className="relative">
                      {renderActionIcon(actionIcon)}
                      {actionIcon.badge && (
                        <span
                          className={cn(
                            'absolute -top-1 -right-1 text-xs rounded-full w-5 h-5 flex items-center justify-center',
                            actionIcon.badgeColor,
                          )}>
                          {actionIcon.badge}
                        </span>
                      )}
                    </div>
                  </OverlayTrigger>
                ))}
                {!isIntroField && (
                  <ActionsMenu newIconLibrary={true}>
                    {actions.map((action, actionIndex) =>
                      action.hide ? null : (
                        <ActionsMenu.Item
                          newIconLibrary={true}
                          key={action.title + actionIndex}
                          icon={action.icon}
                          title={action.title}
                          onClick={() => action.onClick(field)}
                          disabled={action.disabled}
                        />
                      ),
                    )}
                  </ActionsMenu>
                )}
              </>
            )
          )}
        </div>
      </div>
    );

    const sectionContent = (
      <section
        ref={ref}
        className={cn(
          'flex flex-col bg-neutral-1 p-4 rounded-3xl border border-neutral-4 shadow-sm transition-transform duration-200',
          active && !minimal ? 'border-primary-4 shadow-lg' : '',
          isDragging ? 'drop-shadow-2xl -rotate-2' : '',
        )}
        onClick={() => onClick(field, index)}
        keyCode={`${index}`}>
        <Accordion
          defaultCollapsed={!active}
          onToggle={collapsed => setIsCollapsed(collapsed)}
          header={accordionHeader}>
          {/* Expanded body */}
          <div className="flex flex-col gap-8">
            {/* Field type selector and text input */}
            <div className="flex flex-col gap-4 flex-grow-1">
              <div>
                <Select
                  rounded
                  register={register}
                  disabled={isDragDisabled}
                  name={`${registerName}.type`}
                  value={field.type}
                  dataTestId="question-type-select">
                  {FieldTypesOptions.map(fieldType => (
                    <Select.Item
                      key={fieldType.key}
                      value={fieldType.key}
                      description={fieldType.subTitle}
                      testId={`question-type-option-${fieldType.key}`}>
                      <div className="flex items-center gap-2">
                        {fieldType.icon && (
                          <HugeiconsIcon
                            icon={fieldType.icon}
                            size="20"
                            className="text-neutral-7"
                          />
                        )}
                        {fieldType.title}
                      </div>
                    </Select.Item>
                  ))}
                </Select>
              </div>

              {/* Rich text editor for statements, textarea for other types */}
              <div className="flex-1">
                {field.type === FieldTypes.Statement ? (
                  <Controller
                    control={control}
                    name={`${registerName}.text`}
                    rules={{
                      required: 'This field is required.',
                      validate: value => {
                        if (!value) return 'This field is required.';
                        const plainText = value.replace(/<[^>]+>/g, '').trim();
                        return plainText.length > 0 || 'This field is required.';
                      },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RichTextEditor
                        onChange={onChange}
                        value={value}
                        error={error}
                        textParams={textParams}
                        storeTextParamsAsMustache
                      />
                    )}
                  />
                ) : Object.keys(textParams).length > 0 ? (
                  <Controller
                    control={control}
                    name={`${registerName}.text`}
                    rules={{ required: 'This field is required.' }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <ParamTextarea
                        value={value || ''}
                        onChange={onChange}
                        textParams={textParams}
                        placeholder={field.placeholder || 'Enter question...'}
                        error={!!error}
                      />
                    )}
                  />
                ) : (
                  <div className="mt-1">
                    <Textarea
                      register={register}
                      name={`${registerName}.text`}
                      validators={{ required: true }}
                      error={errors?.text?.type}
                      noErrorMessage
                      placeholder={field.placeholder || 'Enter question...'}
                      dynamicHeight={true}
                    />
                  </div>
                )}
              </div>

              {validationErrorMessage && (
                <FadeInMotion disabled={false}>
                  <ValidationAlert show={true} message={validationErrorMessage} />
                </FadeInMotion>
              )}

              <div>{renderFieldSettings()}</div>
              {!field.locked && typesWithLogic.includes(field.type) && (
                <RulesConfiguration
                  control={control}
                  register={register}
                  field={field}
                  rulesCount={rulesCount}
                />
              )}
            </div>
          </div>
        </Accordion>
      </section>
    );

    if (minimal) {
      return <div className={className}>{sectionContent}</div>;
    }

    return (
      <Draggable draggableId={formField.key} index={index} isDragDisabled={isDragDisabled}>
        {({ draggableProps, dragHandleProps, innerRef }) => (
          <div
            {...draggableProps}
            {...dragHandleProps}
            ref={innerRef}
            className={className}
            data-testid="question-row">
            {sectionContent}
          </div>
        )}
      </Draggable>
    );
  },
);

QuestionRow.displayName = 'QuestionRow';

QuestionRow.propTypes = {
  formField: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.number,
    type: PropTypes.string,
    text: PropTypes.string,
    required: PropTypes.bool,
    locked: PropTypes.bool,
    placeholder: PropTypes.string,
    options: PropTypes.shape({
      intro: PropTypes.bool,
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  unregister: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
  duplicate: PropTypes.func.isRequired,
  copyField: PropTypes.func.isRequired,
  pasteFieldAbove: PropTypes.func.isRequired,
  pasteFieldBelow: PropTypes.func.isRequired,
  logicJumpsCount: PropTypes.number,
  errors: PropTypes.object,
  validationErrorMessage: PropTypes.string,
  pasteEnabled: PropTypes.bool,
  textParams: PropTypes.object,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  isDragging: PropTypes.bool,
};

export default QuestionRow;
